/**
 * The spine: four acts, the man from the ownership group, and the ending.
 *
 * Every system the story uses already existed before it did. What is here is
 * the order they come in, the line each act ends on, and the few numbers the
 * ending weighs. `sim/story.js` reads this and nothing else names a beat.
 *
 * Nothing in here is a staff id. The people the story needs are found by what
 * their rows say about them (`finale`, `feeds`, `collects`), the same way the
 * rest of the game finds anybody.
 */

/**
 * The acts, in order, with the condition that ends each one.
 *
 * `departments` is what the act is about, for anybody reading. `ends` is the
 * exit, and it is one of three shapes and nothing more complicated:
 *
 *   played   a beat has been played
 *   warm     every one of these departments is on `warm` or above
 *   flag     the story has set this flag
 *
 * `warm` reads the ledger rather than the card, like a chapter's line does:
 * an act is about what you have earned with a department, and a bad week
 * costing you a door is a different thing from it costing you the story.
 */
export const ACTS = Object.freeze([
  {
    n: 1,
    id: 'new-hire',
    name: 'New Hire',
    departments: ['banquets', 'housekeeping'],
    // Max's pen lives here. It ends when Serg says he is putting your name on things.
    ends: { played: 'ch-banquets-trust' },
  },
  {
    n: 2,
    id: 'back-of-house',
    name: 'Back of House',
    departments: ['kitchen', 'engineering', 'valet'],
    // Danny's dinner, the strike, the lion, Alex's joint.
    ends: { warm: ['kitchen', 'engineering', 'valet'] },
  },
  {
    n: 3,
    id: 'front-of-house',
    name: 'Front of House',
    departments: ['frontdesk', 'management'],
    ends: { warm: ['management'], flag: 'clock-stopped' },
  },
  {
    n: 4,
    id: 'the-threat',
    name: 'The Threat',
    departments: [],
    ends: null,
  },
]);

/**
 * When the man from the ownership group turns up, and how often.
 *
 * Midway through the third act, which is whichever comes first of the two
 * departments the act is about reaching warm, or `arrivesAfter` days of the
 * act going by without either. Waiting on one department would let a player
 * who never goes near the marble hold him off for ever, and he is not the
 * kind of person you hold off.
 *
 * He tours from the day after he arrives, on `days` (Monday is 0), from
 * `from` until `until`. His meetings are at four, every day he tours, and
 * `meets` is where: the staff lot, the manager's office, or nowhere anybody on
 * the floor would see. Where he stands for it is in feet. He comes back on for
 * it at `meeting[0]` and is gone at `meeting[1]`.
 */
export const TOUR = Object.freeze({
  arrivesAfter: 3,
  days: [1, 3, 5],
  from: '10:00',
  until: '15:30',
  meeting: ['15:50', '16:10'],
  meets: {
    1: { room: 'parking', at: [-30, -80] },
    3: { room: 'office', at: [53, -12] },
  },
  /** Where he stands on the day of the finale, all shift, with his tablet. */
  watches: [92, 20],
});

/**
 * The clock over the front desk, and the minute it stops.
 *
 * Four o'clock, on a day he has a meeting, once Management is warm. Nobody
 * says why and nothing on the screen connects it to anything.
 */
export const LOBBY_CLOCK = Object.freeze({ stops: '16:00' });

/**
 * What Grant mentions, in his words, when he happens to have heard something.
 *
 * Keyed by what the claim was about. `{when}` is the day it happened. He never
 * says who told him, because nobody tells him their name either.
 */
export const HEARD = Object.freeze({
  take: 'somebody helping themselves off a shelf',
  break: 'something getting broken',
  trespass: 'somebody where they should not have been',
  uniform: 'blacks on the marble',
  civilian: 'street clothes in the back of house',
  throw: 'things getting thrown about',
  tip: 'a cart going over',
  climb: 'somebody climbing on the fixtures',
  ride: 'somebody riding a cart down a corridor',
  impaired: 'somebody not quite right on shift',
  scent: 'a smell on somebody back of house',
  prank: 'somebody fooling about',
  note: 'a note stuck on the back of somebody',
});

export const HEARD_LINE = 'Heard about {thing}, {when}. Probably nothing, team.';

/**
 * How often he lets one slip, and how many ever.
 *
 * `every` tour days apart at the least, and never more than `most` in a whole
 * week of play. Rare and deniable, or it is a signpost.
 */
export const SLIPS = Object.freeze({ every: 2, most: 3 });

/**
 * The finale: one large event, and Grant in the room for all of it.
 *
 * Ten rounds of eight across the north of the ballroom, short of something
 * from every department that holds stock, and posted the day after Grant says
 * what he wants to do. Its times are the finale day's shift: posted as the
 * shift starts, due `lead` minutes later.
 *
 * `ghosts: false`: nobody sets this one for you. A finale you did not take is
 * a finale that did not happen, and that is weighed as its own outcome.
 */
export const FINALE = Object.freeze({
  id: 'beo-finale',
  code: 'BEO 1400',
  title: 'Ownership Dinner',
  client: 'The ownership group',
  owner: 'banquets',
  type: 'dinner',
  room: 'ballroom',
  covers: 80,
  // The least it is given. In play it is due when a normal player could set
  // it (`normalMinutes` in `sim/beoGen.js`), which on an eight hour shift is
  // as late as the doors can open before the shift ends.
  lead: 330,
  doorsAfter: 30,
  setup: {
    tableType: 'round',
    tableCount: 10,
    chairsPerTable: 8,
    linen: 'ivory',
    layout: { col: 0, row: 0, cols: 5, rows: 2 },
    staging: ['Risers set by the stage', 'Signage at the room entrance'],
  },
  award: { banquets: 22 },
  note: 'Grant will be in the room. Grant will be in the room the whole time.',
  shortfalls: [
    { id: 'linen-short', amount: 4 },
    { id: 'rounds-short', amount: 2 },
    { id: 'chairs-short', amount: 12 },
    { id: 'risers-missing' },
    { id: 'signage-missing' },
  ],
});

/**
 * What each rung of a department does for the finale.
 *
 *   stages    lets go of what it is holding before you ask, and leaves it
 *             where that rung always leaves things: half way, or on the shelf
 *   nothing   leaves it where it is, and you ask like any other day
 *   obstructs holds back more of it, and puts whatever its row says in the way
 *
 * What a department is short of, what it puts in the way, whether it clears
 * the way for everybody else and whether it covers for you with Grant are all
 * on its gate's row, as `finale`.
 */
export const HELP = Object.freeze({
  trusted: 'stages',
  warm: 'stages',
  cold: 'nothing',
  hostile: 'obstructs',
});

/** How many more a hostile department holds back of a counted shortfall. */
export const HOLDS_BACK = 2;

/**
 * The outcome, weighed.
 *
 * Three things, and they are the three the brief names: how the room went,
 * how many departments were behind you, and what Grant had on you. Each is
 * points, the total is compared with `WIN_AT`, and every number is here.
 *
 *   grade    the finale's letter. Not taking it at all is `missed`
 *   behind   per department outside Banquets, by its rung
 *   evidence Grant's file, read from the card and the after-action record,
 *            times Management's cover, one point off per `perPoint` of it
 */
export const OUTCOME = Object.freeze({
  grade: { A: 4, B: 3, C: 2, D: 0, F: -2, missed: -4 },
  behind: { trusted: 2, warm: 1, cold: 0, hostile: -1 },
  evidence: {
    /** A write-up on the card now. */
    card: 3,
    /** A suspension served, ever. */
    served: 5,
    /** Something noticed that cost you: filed, complained about, or named. */
    costly: 2,
    /** Something noticed that stirred somebody, and came to nothing. */
    stirred: 1,
    /** Something he saw himself. */
    collected: 1,
  },
  perPoint: 5,
  winAt: 4,
});

/**
 * Losing, and what it means. It does not end the save.
 *
 * The ownership group does not sack a section on one dinner. It gives the
 * agency a trial: for `days` days the agency staffs the big rooms and you are
 * on the small ones, the same way your supervisor puts you there when he stops
 * carrying you (`assigns.keepUnder` on his row), and Banquets drops a whole
 * step. The crew keep their jobs because a trial is not a contract, which is
 * also what lets the story go on to the question of who fed him. After the
 * trial the board is yours again, and the week is an ordinary week.
 *
 * Winning is Banquets' award for keeping its own rooms.
 */
export const LOSS = Object.freeze({ days: 7 });
export const WIN = Object.freeze({ banquets: 10 });

/** What the right guess is worth: somebody noticed you noticed. */
export const SAW_IT = Object.freeze({ banquets: 5 });

/** What a wrong guess costs the person it lands on, personally. */
export const WRONGED = -40;

/** Covering for him: how many times he lets something of yours go. */
export const OWES = 3;

/** Reporting him: what Management makes of it. */
export const REPORTED = Object.freeze({ management: 8 });

/**
 * Everything said in the last act that is not a department's chapter.
 *
 * `{name}` is the one who fed him, filled in from the roster. `{guess}` is who
 * you named. Each is a panel shaped like a chapter: a title, who says it, and
 * the lines.
 */
export const SCENES = Object.freeze({
  won: {
    title: 'The Numbers',
    speaker: 'Grant Whitfield',
    lines: [
      'Team. Great night. Genuinely.',
      'The room read well and the building was behind you. I noticed that.',
      'I am going to tell the group the agency is off the table.',
      'For now. Everything is for now, team.',
    ],
  },
  lost: {
    title: 'The Numbers',
    speaker: 'Grant Whitfield',
    lines: [
      'Team. I will be straight with you.',
      'The group wants to see what the agency can do. A trial. A week, maybe two.',
      'The big rooms go to them while it runs. You keep the small ones.',
      'Nobody is losing their job tonight. Let us keep it that way.',
    ],
  },
  missed: {
    title: 'The Numbers',
    speaker: 'Grant Whitfield',
    lines: [
      'Team. The dinner happened without you, which is its own answer.',
      'The group wants to see what the agency can do. A trial.',
      'The big rooms go to them while it runs. You keep the small ones.',
      'Nobody is losing their job tonight. Let us keep it that way.',
    ],
  },
  ask: {
    title: 'The Staff Lot, After',
    speaker: 'Sergio "Serg" Alvarez',
    lines: [
      'He knew things. Things that happen in this section and nowhere else.',
      'Somebody has been feeding him. Every week, from the start.',
      'You work beside all of them. Who is it?',
    ],
  },
  saw: {
    title: 'The Staff Lot, After',
    speaker: 'Sergio "Serg" Alvarez',
    lines: [
      'Yeah. That is who I had.',
      'Good eye. Keep it to yourself for now.',
      'There is something you should see first.',
    ],
  },
  truth: {
    title: 'The Front Desk, Closing',
    speaker: 'Priya Raghunathan',
    lines: [
      'He left his tablet on my desk. It was open. I read it. I am not sorry.',
      'Forty pages on this section. Half of it is in the words of one person.',
      'Every day at four, in the lot or the office. It is {name}.',
      'I have sent you the pages. What happens now is up to you.',
    ],
  },
  choose: {
    title: 'The Pages',
    speaker: 'You',
    lines: ['You have the proof on {name}. Cover for him, or report him?'],
  },
  reported: {
    title: 'The Grand Entrance',
    speaker: 'Curtis Hale',
    lines: [
      'I have read it. All of it.',
      '{name} is done here. He can walk out with his friend.',
      'You did the right thing. It will not feel like it for a while.',
      'Back to work. Rooms do not set themselves.',
    ],
  },
  covered: {
    title: 'The Air Wall Pocket',
    speaker: '{name}',
    lines: [
      'You have the pages. I know you have the pages.',
      'He said it was a job. He said the agency would keep me on.',
      'You did not hand them in. I will not forget that.',
      'Whatever you need. I owe you.',
    ],
  },
});

/** The two answers to the last question. */
export const ENDINGS = Object.freeze([
  { id: 'cover', label: 'Cover for him' },
  { id: 'report', label: 'Report him' },
]);
